import { LegacyRangeIds } from '../types';
import { AutoAdaptFeature, AutoAdaptService, ConnectionUtils, ICFDataService, PlacementService, ProppingSystem, RoomComponentDeserializer, StandardSelectionService } from '@inter-ikea-core/core';
import { create_viewer, RenderLayerAllocation } from '@inter-ikea-betrakta/viewer';
import { VpcService } from '@inter-ikea-kompis/services';
import { ComponentIds, HierarchyUtils } from '@inter-ikea-core/core';
import { LightsApi } from './LightsApi';
import { DynamicProductCatalog } from '@inter-ikea-core/core';
import { getLegacyPlannerConfiguration } from '../utils/vpc';
import { createCore } from '@inter-ikea-core/core';
import { IPEXViewerCoreAdapter, setIpexViewer } from '@inter-ikea-core/ipex-betrakta-core-adapter';
import { FixedUpdateSystem } from './FixedUpdateSystem';
import ProductInfoComponent from './components/ProductInfoComponent';
import InvalidPlacementComponent from './components/InvalidPlacementComponent';
import ShoppingBagComponent from './components/ShoppingBagComponent';
import { KvadratRoomApi } from './KvadratRoomApi';
import { EventSystem } from './systems/EventSystem';
import { hasKvadratComponent } from '@inter-ikea-kvadrat/kvadrat';
import { waitForElement } from '../utils/dom';
import { loadSavedDesign } from '../utils/sceneUtils';
import { AssemblyScreenshotHelper } from './screenshotHelper';
import { DragUtils, KvadratVPCComponentSerializer } from '@inter-ikea-core/core-toolbox';
import packageInfo from '../../package.json';
import { serializeEntityRecord } from '../utils/serialize';
import { getEventEmitters } from './events/getEventEmitters';
import { getEmptyListenerMap } from './events/getEmptyListenerMap';
export class PlannerCoreApi {
    constructor(insightsApi, sentryErrorReporter) {
        this._eventSubscribers = getEmptyListenerMap();
        this.registerSystem = (system) => {
            this.core.services.entityManager.registerSystem(system);
        };
        this.registerValidation = (validation) => {
            this.core.addValidation(validation);
        };
        this.getInsightsCombinationFormat = (products, isProduction, dexfApplicationId) => {
            const icfArticles = this.getIcfData(products);
            return {
                icf_version: '1.0',
                application_id: dexfApplicationId,
                application_version: packageInfo.version,
                creation_datetime: new Date(),
                articles: icfArticles,
                meta: {
                    user_generated: isProduction
                }
            };
        };
        this.getCoreConfig = (config) => {
            return {
                catalogs: config.catalogs,
                entities: config.entities,
                version: config.version
            };
        };
        this.core = createCore();
        // TODO replace all these fakes with dummy implementations (or refactor to avoid any need for dummies)
        this.viewer = {};
        this.ipexViewer = {};
        this.conflictResolver = {};
        this.placementService = {};
        this.entities = {};
        this.history = {};
        this.camera = {};
        this.scene = null;
        this.products = {};
        this.lights = null;
        this.kvadratRoom = null;
        this.componentResolvers = [];
        this.catalogs = {};
        this.validations = {};
        this.validationService = this.core.services.validations;
        this.insightsApi = insightsApi;
        this.screenshotHelpers = {};
        this.sentryErrorReporter = sentryErrorReporter;
        this.emit = getEventEmitters(this._eventSubscribers);
        new EventSystem(this);
    }
    getInteriorSuggestionProductCatalog() {
        const interiorSuggestionKey = Object.keys(this.catalogs).find(key => key.includes('interior-suggestions'));
        return interiorSuggestionKey ? this.catalogs[interiorSuggestionKey] : null;
    }
    getFrontLayoutProductCatalog() {
        const frontLayoutKey = Object.keys(this.catalogs).find(key => key.includes('front-layouts'));
        return frontLayoutKey ? this.catalogs[frontLayoutKey] : null;
    }
    initializeBaseCoreFunctionality() {
        this.core.setLogModule();
        this.core.addHistoryModule();
        const selectionService = new StandardSelectionService(this.core.services.entityManager);
        const autoAdaptService = new AutoAdaptService(this.core.services);
        const placementService = new PlacementService(this.core.services, autoAdaptService);
        this.placementService = placementService;
        this.registerSystem(DragUtils.createAutoAdaptDragSystem(this.core.services));
        AutoAdaptFeature.start(this.core.services);
        this.core.services.componentDeserializerRegistry.register(new KvadratVPCComponentSerializer());
        this.core.services.componentDeserializerRegistry.register(new RoomComponentDeserializer());
        return { selectionService, placementService, autoAdaptService };
    }
    async loadProductCatalogs(catalogs) {
        await Promise.all(catalogs.map(async (catalog) => {
            await this.loadCatalog(catalog);
        }));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async loadProppingCatalog(productCatalog, ageGroup) {
        if (this.catalogs['propping']) {
            this.core.services.productManager.removeCatalog(this.catalogs['propping']);
        }
        this.catalogs['propping'] = new DynamicProductCatalog(JSON.stringify(productCatalog.catalog), JSON.stringify(productCatalog.template), productCatalog.assetsPath);
        this.core.services.productManager.addCatalog(this.catalogs['propping']);
        const existingProppingSystem = this.getSystem('ProppingSystem');
        if (existingProppingSystem) {
            existingProppingSystem.dispose();
            this.core.services.entityManager.unregisterSystem(existingProppingSystem);
        }
        this.core.services.entityManager.registerSystem(new ProppingSystem(this.catalogs['propping'], this.core.services));
    }
    async loadCatalog(productCatalog) {
        if (productCatalog.name.toLocaleLowerCase() === 'propping') {
            this.loadProppingCatalog(productCatalog);
        }
        else {
            const name = productCatalog.name;
            if (this.catalogs[name]) {
                this.core.services.productManager.removeCatalog(this.catalogs[name]);
            }
            this.catalogs[name] = new DynamicProductCatalog(JSON.stringify(productCatalog.catalog), JSON.stringify(productCatalog.template), productCatalog.assetsPath);
            this.core.services.productManager.addCatalog(this.catalogs[name]);
        }
    }
    getServices() {
        return this.core.services;
    }
    getModules() {
        return this.core.services.coreModules;
    }
    getSystem(id) {
        const system = this.getServices()
            .entityManager.getSystemsRegistered()
            .find(s => s.id === id);
        return system || null;
    }
    registerResolvers(config, products, unavailableProducts, productsNotInMarket, coreSpecificProducts, isMobile) {
        if (this.componentResolvers.length > 0) {
            this.componentResolvers.forEach(resolver => this.core.services.productManager.unsubscribeProductCreated(resolver));
        }
        if (products.length === 0) {
            return;
        }
        const resolvers = config.componentResolvers?.map(resolver => resolver(this, products, unavailableProducts, productsNotInMarket, coreSpecificProducts, isMobile)) || [];
        this.componentResolvers = resolvers.map(resolver => {
            return (entity) => resolver.resolveComponentBuilder().build(entity);
        });
        this.componentResolvers.forEach(resolver => {
            this.core.services.productManager.subscribeProductCreated(resolver);
        });
    }
    getIcfData(productList) {
        // Returns all the colors for a specified product
        const getColors = (appearances) => {
            return appearances.map(appearance => appearance.color);
        };
        // Converts our positions to ICF accepted data structure
        const vector3 = (vector) => {
            return {
                x: vector.X,
                y: vector.Y,
                z: vector.Z
            };
        };
        // We can't send negative values to the icf service for rototaion so we need to make a simple normalization
        const normalizeRotation = (rotationValue) => {
            // We need to round the value to avoid floating point errors
            return (Math.round(rotationValue) + 360) % 360;
        };
        const rotation = (vector) => {
            return {
                x: normalizeRotation(vector.X),
                y: normalizeRotation(vector.Y),
                z: normalizeRotation(vector.Z)
            };
        };
        // We use the colliderDimensions if they exists as those are more exact than the communicatedSize dimensions
        // But in some cases the colliderDimensions does not exists and we need to fall back to the communicatedSize
        const getDimensions = (communicatedSize, colliderDimensions) => {
            if (!colliderDimensions ||
                (colliderDimensions.X === 0 && colliderDimensions.Y === 0 && colliderDimensions.Y === 0)) {
                if (!communicatedSize) {
                    return {
                        x: 0,
                        y: 0,
                        z: 0
                    };
                }
                return {
                    x: communicatedSize.width,
                    y: communicatedSize.height,
                    z: communicatedSize.depth
                };
            }
            else {
                return vector3(colliderDimensions);
            }
        };
        const icfDataService = new ICFDataService(this.core.services.sceneManager);
        // We use the ShoppingBagComponentId to search for entities so we don't end up with a bunch of assemblies in the icf data.
        const allEntitiesInShoppingBag = this.entities.getByComponent(ShoppingBagComponent.id);
        const entityIds = allEntitiesInShoppingBag.map(entity => entity.id);
        const icfData = icfDataService.getICFDataForEntities(entityIds);
        const icfProducts = icfData.map((icfData) => {
            // Create ICF Products based on the data from Core
            const productRef = icfData.entity.getComponent(ComponentIds.PRODUCT_COMPONENT_ID)?.productRef;
            // Find the matching product from our list of products
            const matchingProductFromProductList = productList.find((p) => p.coreProductId === productRef);
            return {
                id: icfData.id,
                parent_ids: icfData.parent_ids,
                child_ids: Array.from(new Set(icfData.children_ids)),
                product_id: matchingProductFromProductList?.globalItemNo || productRef,
                name: icfData.entity.getComponent(ComponentIds.DISPLAY_NAME_COMPONENT_ID)?.name,
                category: icfData.entity.getComponent(ProductInfoComponent.id)
                    ?.productType,
                colors: matchingProductFromProductList
                    ? getColors(matchingProductFromProductList.appearances)
                    : [],
                // Should contain metadata if we want to, but not sure what to put here
                attributes: {},
                dimensions: getDimensions(icfData.entity.getComponent(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)?.size || { width: 0, height: 0, depth: 0 }, icfData.colliderDimensions),
                transform: {
                    position: vector3(icfData.transform.position),
                    rotation: rotation(icfData.transform.rotation.toEulerDegrees()),
                    scale: { x: 1, y: 1, z: 1 }
                }
            };
        });
        return icfProducts;
    }
    async saveVpc(settings, shoppingItems, images, products, isProduction, options) {
        this.emit.onSaveVpcStart();
        if (options?.saveProppingEntities) {
            const propping = this.entities.getByComponent(ComponentIds.PROPPING_COMPONENT_ID);
            propping.forEach(entity => entity.removeComponent(ComponentIds.UNSERIALIZABLE_ENTITY_COMPONENT_ID));
        }
        const service = new VpcService(settings);
        const sceneManager = JSON.parse(this.saveScene());
        const insightsCombinationFormat = await this.getInsightsCombinationFormat(products, isProduction, settings.dexfApplicationId);
        const invalidEntities = this.entities
            .getByComponent(InvalidPlacementComponent.id)
            .map(({ id }) => id);
        const entities = sceneManager.entities.filter((entity) => !invalidEntities.includes(entity.id) && !invalidEntities.includes(entity.parent));
        const kvadratVpc = await this.kvadratRoom?.saveBlueprint();
        let configuration = { ...sceneManager, entities, kvadratVpc };
        if (options?.proppingAgeGroup) {
            configuration = { ...configuration, proppingAgeGroup: options.proppingAgeGroup };
        }
        if (options?.wallShape) {
            configuration = { ...configuration, wallShape: options.wallShape };
        }
        const response = await service.saveConfiguration(configuration, shoppingItems, images, 
        // @ts-ignore
        { ...insightsCombinationFormat });
        if (response) {
            this.emit.onSaveVpcComplete(response.configurationId);
        }
        return response;
    }
    async loadVpc(vpcCode, settings, range, dexfApiKey, itemListEndpoint) {
        const service = new VpcService(settings);
        let response;
        try {
            this.emit.onLoadVpc();
            response = await service.getConfiguration(vpcCode);
            if (response.application in LegacyRangeIds && dexfApiKey && itemListEndpoint) {
                response = await getLegacyPlannerConfiguration(response, dexfApiKey, itemListEndpoint, settings, (legacyShoppingItems) => this.emit.onLoadLegacyShoppingItems(legacyShoppingItems));
                if (!response) {
                    this.emit.onLoadVpcFailure(vpcCode);
                    return null;
                }
                return response;
            }
            if (response.configuration.proppingAgeGroup) {
                this.emit.onProppingAgeGroupUpdated(response.configuration.proppingAgeGroup);
            }
            this.emit.onLoadVpcSuccess();
            return response;
        }
        catch (error) {
            this.emit.onLoadVpcFailure(vpcCode);
            // eslint-disable-next-line no-console
            console.error(error);
            return null;
        }
    }
    subscribe(type, listener) {
        this._eventSubscribers[type].add(listener);
        return () => {
            this._eventSubscribers[type].delete(listener);
        };
    }
    createViewer(canvas, renderSettings, applicationName, locale, dexfBaseUrl, dexfApiKey, isProduction, enableFeatures, roomOptions) {
        // TODO: Remove once these files are hosted on Akamai and viewer uses that as default
        const baseUrl = import.meta.env.BASE_URL;
        const prepend = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
        const config = {
            draco_decoder: {
                wasmUrl: `${prepend}/draco_wasm_wrapper_gltf.js`,
                wasmBinaryUrl: `${prepend}/draco_decoder_gltf.wasm`,
                fallbackUrl: `${prepend}/draco_decoder_gltf.js`
            }
        };
        this.ipexViewer = create_viewer(canvas, config.draco_decoder);
        this.screenshotHelpers = new AssemblyScreenshotHelper(this.ipexViewer, RenderLayerAllocation.IPEX_PLANNER);
        this.viewer = new IPEXViewerCoreAdapter(this.ipexViewer, this.core.services.entityManager, this.core.services.sceneManager);
        this.initSceneApi();
        this.entities.setIpexViewer(this.ipexViewer);
        this.products.setIpexViewer(this.ipexViewer);
        this.core.setViewer(this.viewer);
        setIpexViewer(this.getServices().sceneManager, this.getServices().entityManager, this.ipexViewer);
        this.lights = new LightsApi(this.ipexViewer, renderSettings);
        this.kvadratRoom = new KvadratRoomApi(this.core.services, this.ipexViewer, this.insightsApi, roomOptions?.dimensions);
        this.initCameraApi(canvas);
        this.initFixedUpdateSystem();
        return this.viewer;
    }
    initFixedUpdateSystem() {
        const componentListeners = new FixedUpdateSystem(this.getServices(), this.ipexViewer, this.entities, (productEntitiesWithPosition) => {
            this.emit.onUpdate(productEntitiesWithPosition);
        });
        componentListeners.register();
    }
    async performLongCoreOperation(coreOperation) {
        this.emit.onSolutionUpdateInProgress();
        // Wait maximum 500ms for loader
        await waitForElement('#loading-indicator', 500).catch(() => { });
        await new Promise(resolve => setTimeout(() => resolve(coreOperation()), 20));
        this.emit.onSolutionUpdateDone();
    }
    saveScene() {
        return this.core.services.sceneManager.save(this.core.services);
    }
    loadKvadratRoom() {
        const roomGeometry = HierarchyUtils.children(this.core.services.sceneManager.scene).find(child => child.hasComponent(ComponentIds.ROOM_GEOMETRY_COMPONENT_ID));
        if (!roomGeometry)
            return;
        const roomDimensions = roomGeometry.getComponent(ComponentIds.ROOM_GEOMETRY_COMPONENT_ID)?.dimensions;
        if (!roomDimensions)
            return;
        if (!this.kvadratRoom)
            return;
        this.kvadratRoom.setRoomSize(roomDimensions.size.X, roomDimensions.size.Y, roomDimensions.size.Z);
    }
    reportErrorToSentry(error, extras, callback) {
        const serialized = extras?.entitiesToSerialize
            ? serializeEntityRecord(this.core.services.serializer, extras.entitiesToSerialize)
            : undefined;
        this.sentryErrorReporter(error, { ...extras, serializedEntities: serialized }, callback);
        return error;
    }
    async loadScene(config, setRoom) {
        // Disable WallVisibilityValidation so it doesn't hinder frames from snapping to hidden walls during load
        this.validations['WallVisibilityValidation']?.disable();
        this.getSystem('DistanceMeasurementSystem')?.disable();
        // Do not trigger history events when loading
        this.getSystem('HistoryEventSystem')?.disable();
        this.getSystem('ProppingSystem')?.disable();
        await loadSavedDesign(config, setRoom, this.getServices(), this.kvadratRoom, this.placementService);
        const centerPositionOfAllRoomPartsInScene = this.kvadratRoom?.getRoomCenterPosition();
        if (centerPositionOfAllRoomPartsInScene) {
            this.lights?.centerLights(centerPositionOfAllRoomPartsInScene);
        }
        this.getSystem('DistanceMeasurementSystem')?.enable();
        this.validations['WallVisibilityValidation']?.enable();
        this.getSystem('HistoryEventSystem')?.enable();
        this.getSystem('ProppingSystem')?.enable();
        //Load legacy rooms in kvadrat
        //this.loadKvadratRoom();
        this.emit.onLoad();
        this.emit.onLoadWallShapeFromVpc(config);
    }
    resetEntitiesInScene() {
        const scene = this.entities.getScene();
        const entitiesToDelete = scene
            ? HierarchyUtils.descendants(scene).filter(e => !hasKvadratComponent(e))
            : [];
        ConnectionUtils.disconnectAndDisposeEntitiesAndHierarchies(entitiesToDelete);
    }
    resetScene() {
        this.emit.onReset();
        this.resetEntitiesInScene();
        this.history.clear();
        this.kvadratRoom?.resetRoom();
        this.resetLights();
    }
    resetLights() {
        const centerPosition = this.kvadratRoom?.getRoomCenterPosition();
        centerPosition && this.lights?.centerLights(centerPosition);
    }
    resetSceneSpace() {
        this.resetEntitiesInScene();
        this.history.clear();
    }
    getRangeProducts() {
        return Object.values(this.catalogs).flatMap(c => c.productDTOs());
    }
    getRangeProductForEntity(entity) {
        if (!entity)
            return null;
        const isProduced = entity.hasComponent(ComponentIds.PRODUCT_COMPONENT_ID);
        if (!isProduced)
            return null;
        const result = this.getRangeProducts().find((rp) => rp.id ===
            entity.getComponent(ComponentIds.PRODUCT_COMPONENT_ID)?.productRef);
        return result ? result : null;
    }
    getPartsOfRangeProduct(entity) {
        if (entity.hasComponent(ComponentIds.PARENT_COMPONENT_ID)) {
            return HierarchyUtils.hierarchy(entity);
        }
        else {
            return [entity];
        }
    }
    toggleExhaustiveValidation(status) {
        const collisionValidation = this.core
            .getValidations()
            .find(v => v.id === 'Collision');
        if (status) {
            collisionValidation?.enableExhaustiveValidation();
        }
        else {
            collisionValidation?.disableExhaustiveValidation();
        }
    }
    createConflict(placementResult, entitiesToAddToCollidingEntityProductIds, collidingEntityAssembly) {
        const errors = placementResult.errors;
        let conflicts = [];
        if (errors.length > 0) {
            conflicts = errors.flatMap((error) => {
                const collisionValidationResult = error;
                const productRef = collisionValidationResult.productRef;
                // We currently only handle collision warnings
                if (!('collisions' in error)) {
                    return [];
                }
                const conflictingEntities = collisionValidationResult.collisions.flatMap(collision => {
                    let targetEntity = collision.other;
                    let collidingEntity = collision.self;
                    if (collision.other.hasComponent(ComponentIds.CHILD_COMPONENT_ID)) {
                        const root = this.entities.getAssemblyRoot(collision.other);
                        if (root != null) {
                            targetEntity = root;
                        }
                    }
                    if (collision.self.hasComponent(ComponentIds.CHILD_COMPONENT_ID)) {
                        const root = this.entities.getAssemblyRoot(collision.self);
                        if (root != null) {
                            collidingEntity = root;
                        }
                    }
                    // TODO: Remove any cast when Core has updated their type definitions
                    return {
                        targetEntity: targetEntity, // entity already in scene
                        collidingEntity: collidingEntity, // entity that failed to be added
                        position: collision.selfWorldTransformAtTimeOfCollision.position
                    };
                });
                const conflictList = conflictingEntities.flatMap(({ targetEntity, collidingEntity, position }) => {
                    const conflict = this.conflictResolver.createConflict({
                        targetEntity,
                        collidingEntity,
                        position,
                        collidingEntityProductRef: productRef,
                        entitiesToAddToCollidingEntityProductIds,
                        collidingEntityAssembly
                    });
                    return conflict || [];
                });
                return conflictList;
            });
            if (conflicts.length > 0) {
                this.emit.onConflict(conflicts);
            }
        }
        else {
            // eslint-disable-next-line no-console
            console.warn('Unhandled conflict', errors);
        }
    }
}
//# sourceMappingURL=PlannerCoreApi.js.map